import type { GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import { zoneMapQuerySchema, type ColorMode } from '@chukua/common';
import { useEffect, useState } from 'react';
import { parseListParams } from '../../../shared/policies/list-params';
import { useZoneKinds } from '../../reference-data/features/zone-kinds/public/use-zone-kinds';
import { ZONE_POINTS_SOURCE, ZONE_POINTS_SOURCE_ID, ZONE_SHAPES_SOURCE_ID, zoneLayers } from '../features/map/adapters/zones-map-layers';
import { zonesMapSources } from '../features/map/policies/zones-map-data';
import type { ZoneKind } from '../../reference-data/reference-data.api';
import { listZonesForMap, type Zone } from '../zones.api';

/** One array, so the layers are not rebuilt on every render while the kinds are still loading. */
const NO_KINDS: ZoneKind[] = [];
const EVERY_ZONE = parseListParams(zoneMapQuerySchema, new URLSearchParams());

/**
 * Draws the company's zones on another unit's already-loaded map (the live map, gitaumoses4/chukua#100), with the colours and
 * layers the Zones map uses, so a place looks the same in both. A zone that cannot be loaded leaves the map without its overlay:
 * the trucks are the point of the live map, the zones are context. Never clickable here. The zone layers go under `beforeLayerId`
 * when the caller has that layer, because they are re-added when the kinds arrive and must not cover what the caller drew first.
 */
export function useZonesOverlay(map: MapLibreMap | null, mode: ColorMode, beforeLayerId?: string): void {
  const [zones, setZones] = useState<Zone[]>([]);
  const kinds = useZoneKinds();
  const zoneKinds = kinds.state.status === 'loaded' ? kinds.state.kinds : NO_KINDS;

  useEffect(() => {
    let cancelled = false;
    listZonesForMap(EVERY_ZONE)
      .then((loaded) => {
        if (!cancelled) setZones(loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    const { shapes, points } = zonesMapSources([]);
    map.addSource(ZONE_SHAPES_SOURCE_ID, { type: 'geojson', data: shapes });
    map.addSource(ZONE_POINTS_SOURCE_ID, { ...ZONE_POINTS_SOURCE, data: points });
    const before = beforeLayerId && map.getLayer(beforeLayerId) ? beforeLayerId : undefined;
    zoneLayers(zoneKinds, { mode, isDimmed: false }).forEach((layer) => map.addLayer(layer, before));
    // The map is rebuilt, not repainted, when the colour mode or the kinds change, so its sources go with it.
    return () => {
      if (!map.getStyle()) return;
      for (const layer of zoneLayers(zoneKinds, { mode, isDimmed: false })) if (map.getLayer(layer.id)) map.removeLayer(layer.id);
      for (const id of [ZONE_SHAPES_SOURCE_ID, ZONE_POINTS_SOURCE_ID]) if (map.getSource(id)) map.removeSource(id);
    };
  }, [map, mode, zoneKinds, beforeLayerId]);

  useEffect(() => {
    if (!map) return;
    const { shapes, points } = zonesMapSources(zones);
    map.getSource<GeoJSONSource>(ZONE_SHAPES_SOURCE_ID)?.setData(shapes);
    map.getSource<GeoJSONSource>(ZONE_POINTS_SOURCE_ID)?.setData(points);
  }, [map, zones, zoneKinds, mode]);
}
